import { Row, Col, Form } from "react-bootstrap";
import { UPLOADS_BASE } from "../constants/api";
import {
  filterChildLocations,
  filterCountries,
  findLocationTypeId,
} from "../../../utils/locationHelpers";
import { calculateAgeFromDob } from "../utils/patientFormHelpers";

const COL = 6;

const PatSectionCard = ({ title, sectionNumber, children }) => (
  <div className="em-section-card">
    {title && (
      <h4 className="em-section-title">
        {sectionNumber != null && <span className="em-section-num">{sectionNumber}. </span>}
        {title}
      </h4>
    )}
    {children}
  </div>
);

const PatRow = ({ children }) => (
  <Row className="em-row em-row--two-col">{children}</Row>
);

const PatPhoneList = ({ formData, onChange }) => {
  const phones = Array.isArray(formData.phone_numbers) && formData.phone_numbers.length
    ? formData.phone_numbers
    : [""];

  const syncPhones = (next) => onChange("phone_numbers", next);

  return (
    <div className="em-phone-list">
      {phones.map((phone, idx) => (
        <div key={idx} className="em-phone-row">
          <Form.Control
            type="text"
            placeholder="Phone number"
            value={phone}
            onChange={(e) => {
              const next = [...phones];
              next[idx] = e.target.value;
              syncPhones(next);
            }}
          />
          {phones.length > 1 && (
            <button
              type="button"
              className="em-btn-remove"
              onClick={() => syncPhones(phones.filter((_, i) => i !== idx))}
              aria-label="Remove phone"
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button type="button" className="em-btn-add" onClick={() => syncPhones([...phones, ""])}>
        + Add more
      </button>
    </div>
  );
};

const GENDER_OPTIONS = ["Male", "Female", "Other"];

const PatientMasterForm = ({
  formData,
  onChange,
  doctors = [],
  bloodGroups = [],
  relationships = [],
  locationTypes = [],
  locations = [],
}) => {
  const stateTypeId = findLocationTypeId(locationTypes, "state", "province");
  const cityTypeId = findLocationTypeId(locationTypes, "city", "district");
  const countries = filterCountries(locations, locationTypes);
  const residenceLocations = filterChildLocations(locations, formData.country_id);
  const states = locations.filter(
    (l) =>
      String(l.location_type_id) === String(stateTypeId) &&
      String(l.parent_location_id) === String(formData.country_id)
  );
  const cityParentId = formData.state_id || formData.country_id;
  const cities = locations.filter(
    (l) =>
      String(l.location_type_id) === String(cityTypeId) &&
      cityParentId &&
      String(l.parent_location_id) === String(cityParentId)
  );

  const countryCodeOptions = countries
    .filter((c) => c.country_code)
    .map((c) => ({
      code: c.country_code,
      label: `${c.country_code} (${c.location_name})`,
    }));

  const handleResidenceCountryChange = (countryId) => {
    onChange("country_id", countryId);
    onChange("location_id", "");
    onChange("state_id", "");
    onChange("city_id", "");
    const country = countries.find((c) => String(c.location_id) === String(countryId));
    if (country?.country_code) onChange("country_code", country.country_code);
    else onChange("country_code", "");
  };

  const handleDobChange = (value) => {
    onChange("date_of_birth", value);
    onChange("age_years", value ? calculateAgeFromDob(value) : "");
  };

  const hasDob = !!formData.date_of_birth;
  const displayAge = hasDob
    ? (formData.age_years || calculateAgeFromDob(formData.date_of_birth))
    : (formData.age_years || "");

  const renderEmergencyContact = (prefix, title) => (
    <div className="em-emergency-block">
      <h5 className="em-subsection-title">{title}</h5>
      <PatRow>
        <Form.Group as={Col} md={COL} controlId={`${prefix}_name`}>
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Contact name"
            value={formData[`${prefix}_name`] || ""}
            onChange={(e) => onChange(`${prefix}_name`, e.target.value)}
          />
        </Form.Group>
        <Form.Group as={Col} md={COL} controlId={`${prefix}_relationship`}>
          <Form.Label>Relationship</Form.Label>
          <Form.Select
            value={formData[`${prefix}_relationship_id`] || ""}
            onChange={(e) => onChange(`${prefix}_relationship_id`, e.target.value)}
          >
            <option value="">Select relationship</option>
            {relationships.map((r) => (
              <option key={r.relationship_id} value={r.relationship_id}>
                {r.relationship}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </PatRow>
      <PatRow>
        <Form.Group as={Col} md={COL} controlId={`${prefix}_country_code`}>
          <Form.Label>Country Code</Form.Label>
          <Form.Select
            value={formData[`${prefix}_country_code`] || ""}
            onChange={(e) => onChange(`${prefix}_country_code`, e.target.value)}
          >
            <option value="">Select code</option>
            {countryCodeOptions.map((opt) => (
              <option key={`${prefix}-${opt.code}`} value={opt.code}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md={COL} controlId={`${prefix}_phone`}>
          <Form.Label>Phone No</Form.Label>
          <Form.Control
            type="text"
            placeholder="Phone number"
            value={formData[`${prefix}_phone`] || ""}
            onChange={(e) => onChange(`${prefix}_phone`, e.target.value)}
          />
        </Form.Group>
      </PatRow>
      <PatRow>
        <Form.Group as={Col} md={COL} controlId={`${prefix}_email`}>
          <Form.Label>Email Address</Form.Label>
          <Form.Control
            type="email"
            placeholder="email@example.com"
            value={formData[`${prefix}_email`] || ""}
            onChange={(e) => onChange(`${prefix}_email`, e.target.value)}
          />
        </Form.Group>
      </PatRow>
    </div>
  );

  return (
    <div className="em-form em-form--patient">
      <PatSectionCard title="Patient Information" sectionNumber={1}>
        <PatRow>
          <Form.Group as={Col} md={COL} controlId="patientProfilePic">
            <Form.Label>Patient Profile Picture</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => onChange("profile_pic", e.target.files[0])}
            />
            {(formData.profile_pic instanceof File || formData.profile_pic_path) && (
              <div className="em-image-preview mt-2">
                <img
                  src={
                    formData.profile_pic instanceof File
                      ? URL.createObjectURL(formData.profile_pic)
                      : `${UPLOADS_BASE}/uploads/${formData.profile_pic_path}`
                  }
                  alt="Patient profile preview"
                  style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }}
                />
              </div>
            )}
          </Form.Group>
          <Form.Group as={Col} md={COL} controlId="patientName">
            <Form.Label>Patient Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Full name"
              value={formData.patient_name || ""}
              onChange={(e) => onChange("patient_name", e.target.value)}
            />
            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
          </Form.Group>
        </PatRow>
        <PatRow>
          <Form.Group as={Col} md={COL} controlId="gender">
            <Form.Label>Gender</Form.Label>
            <Form.Select
              value={formData.gender || ""}
              onChange={(e) => onChange("gender", e.target.value)}
            >
              <option value="">Select gender</option>
              {GENDER_OPTIONS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} md={COL} controlId="dateOfBirth">
            <Form.Label>Date of Birth</Form.Label>
            <Form.Control
              type="date"
              value={formData.date_of_birth || ""}
              onChange={(e) => handleDobChange(e.target.value)}
            />
          </Form.Group>
        </PatRow>
        <PatRow>
          <Form.Group as={Col} md={COL} controlId="ageYears">
            <Form.Label>Age in Years</Form.Label>
            <Form.Control
              type="number"
              min="0"
              max="150"
              placeholder={hasDob ? "" : "If DOB not available"}
              value={displayAge}
              onChange={(e) => onChange("age_years", e.target.value)}
              readOnly={hasDob}
            />
            <span className="em-field-hint">
              {hasDob
                ? "Calculated automatically from date of birth"
                : "Use when date of birth is not available"}
            </span>
          </Form.Group>
          <Form.Group as={Col} md={COL} controlId="healthPassport">
            <Form.Label>Health Passport No</Form.Label>
            <Form.Control
              type="text"
              placeholder="Passport number"
              value={formData.health_passport_no || ""}
              onChange={(e) => onChange("health_passport_no", e.target.value)}
            />
          </Form.Group>
        </PatRow>
        <PatRow>
          <Form.Group as={Col} md={COL} controlId="referredBy">
            <Form.Label>Referred by (Doctor)</Form.Label>
            <Form.Select
              value={formData.referred_by_doctor_id || ""}
              onChange={(e) => onChange("referred_by_doctor_id", e.target.value)}
            >
              <option value="">Select doctor</option>
              {doctors.map((d) => (
                <option key={d.doctor_id} value={d.doctor_id}>
                  {d.doctor_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} md={COL} controlId="referredDate">
            <Form.Label>Referred Date</Form.Label>
            <Form.Control
              type="date"
              value={formData.referred_date || ""}
              onChange={(e) => onChange("referred_date", e.target.value)}
            />
          </Form.Group>
        </PatRow>
        <PatRow>
          <Form.Group as={Col} md={COL} controlId="bloodGroup">
            <Form.Label>Blood Group</Form.Label>
            <Form.Select
              value={formData.blood_group_id || ""}
              onChange={(e) => onChange("blood_group_id", e.target.value)}
            >
              <option value="">Select blood group</option>
              {bloodGroups.map((bg) => (
                <option key={bg.blood_group_id} value={bg.blood_group_id}>
                  {bg.blood_group}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} md={COL} controlId="nationality">
            <Form.Label>Nationality</Form.Label>
            <Form.Select
              value={formData.nationality_id || ""}
              onChange={(e) => onChange("nationality_id", e.target.value)}
            >
              <option value="">Select country</option>
              {countries.map((c) => (
                <option key={c.location_id} value={c.location_id}>
                  {c.location_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </PatRow>
        <PatRow>
          <Form.Group as={Col} md={COL} controlId="residenceCountry">
            <Form.Label>Country of Residence</Form.Label>
            <Form.Select
              value={formData.country_id || ""}
              onChange={(e) => handleResidenceCountryChange(e.target.value)}
            >
              <option value="">Select country</option>
              {countries.map((c) => (
                <option key={c.location_id} value={c.location_id}>
                  {c.location_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} md={COL} controlId="location">
            <Form.Label>Location</Form.Label>
            <Form.Select
              value={formData.location_id || ""}
              onChange={(e) => onChange("location_id", e.target.value)}
              disabled={!formData.country_id}
            >
              <option value="">Select location</option>
              {residenceLocations.map((l) => (
                <option key={l.location_id} value={l.location_id}>
                  {l.location_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </PatRow>
      </PatSectionCard>

      <PatSectionCard title="Contact Information" sectionNumber={2}>
        <PatRow>
          <Form.Group as={Col} md={COL} controlId="addressLine1">
            <Form.Label>Address Line 1</Form.Label>
            <Form.Control
              type="text"
              placeholder="Street, building, area"
              value={formData.address_line_1 || ""}
              onChange={(e) => onChange("address_line_1", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md={COL} controlId="addressLine2">
            <Form.Label>Address Line 2</Form.Label>
            <Form.Control
              type="text"
              placeholder="Landmark, suite, floor"
              value={formData.address_line_2 || ""}
              onChange={(e) => onChange("address_line_2", e.target.value)}
            />
          </Form.Group>
        </PatRow>
        <PatRow>
          <Form.Group as={Col} md={COL} controlId="state">
            <Form.Label>State / Province</Form.Label>
            <Form.Select
              value={formData.state_id || ""}
              onChange={(e) => {
                onChange("state_id", e.target.value);
                onChange("city_id", "");
              }}
              disabled={!formData.country_id}
            >
              <option value="">Select state</option>
              {states.map((s) => (
                <option key={s.location_id} value={s.location_id}>
                  {s.location_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} md={COL} controlId="city">
            <Form.Label>City</Form.Label>
            <Form.Select
              value={formData.city_id || ""}
              onChange={(e) => onChange("city_id", e.target.value)}
              disabled={!formData.country_id}
            >
              <option value="">Select city</option>
              {cities.map((c) => (
                <option key={c.location_id} value={c.location_id}>
                  {c.location_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </PatRow>
        <PatRow>
          <Form.Group as={Col} md={COL} controlId="postalCode">
            <Form.Label>Postal Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="PIN / ZIP"
              value={formData.postal_code || ""}
              onChange={(e) => onChange("postal_code", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md={COL} controlId="countryCode">
            <Form.Label>Country Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="+91"
              value={formData.country_code || ""}
              readOnly={!!formData.country_id}
            />
            <span className="em-field-hint">Auto-filled from country of residence</span>
          </Form.Group>
        </PatRow>
        <PatRow>
          <Form.Group as={Col} md={COL} controlId="phoneNumbers">
            <Form.Label>Phone No</Form.Label>
            <PatPhoneList formData={formData} onChange={onChange} />
          </Form.Group>
          <Form.Group as={Col} md={COL} controlId="email">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              placeholder="email@example.com"
              value={formData.email_address || ""}
              onChange={(e) => onChange("email_address", e.target.value)}
            />
          </Form.Group>
        </PatRow>
      </PatSectionCard>

      <PatSectionCard title="Emergency Contact Information" sectionNumber={3}>
        {renderEmergencyContact("emergency_contact_1", "Emergency Contact 1")}
        {renderEmergencyContact("emergency_contact_2", "Emergency Contact 2")}
      </PatSectionCard>
    </div>
  );
};

export default PatientMasterForm;
